import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { SelectionModel } from '@angular/cdk/collections';
import { tap } from 'rxjs/operators';
import { MatSort } from '@angular/material/sort';
import { MatLegacyTable as MatTable } from '@angular/material/legacy-table';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { LegFiJwtService } from '../../../../../services/auth/legfi-jwt.service';
import { JwtLegFiClaims } from '../../../../../services/auth/jwt-legfi-claims.model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { merge } from 'rxjs';
import { PaymentGroup } from '../../../../../models/entities/payment-group';
import { AdminPaymentsDatasource, PaymentsDatasource, PaymentsPageSizeOptions, PaymentsPaginationMeta } from '../../../../../services/payment/payments.datasource';
import { Transaction } from 'app/models/entities/transactions/transaction';
import { DepositDetailDialogComponent, DepositDetailDialogInput } from 'app/components/app-layout/shared/deposit-detail-dialog/deposit-detail-dialog.component';
import { MatDialogSizes } from 'app/enums/mat-dialog-sizes.enum';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { TransactionsResponse, TransactionsService } from 'app/services/accounting/transactions.service';
import { LoadingDialogComponent, LoadingDialogInput } from 'app/components/app-layout/shared/loading-dialog/loading-dialog.component';

@UntilDestroy()
@Component({
    selector: 'app-payments-table',
    templateUrl: './payments-table.component.html',
    styleUrls: ['./payments-table.component.scss'],
})
export class PaymentsTableComponent implements OnInit, AfterViewInit
{
    @Input() tableDataSource: PaymentsDatasource | AdminPaymentsDatasource;
    @Input() selection: SelectionModel<PaymentGroup> = null;
    @Input() totalRecords = 0;
    @Input() isOnDashboard = false;
    @Input() isOnUnitDashboard = false;
    @Input() isOnAdmin = false;
    @Input() isOnMapModal = false;

    @Output() loadPayments: EventEmitter<void> = new EventEmitter<void>();
    @Output() selectionMade: EventEmitter<any> = new EventEmitter<any>();

    @ViewChild(MatTable) table: MatTable<any>;
    @ViewChild(MatPaginator) matPaginator: MatPaginator;
    @ViewChild(MatSort) sort: MatSort;

    isOnMaster = false;

    jwt: JwtLegFiClaims;

    pageSizeOptions = PaymentsPageSizeOptions;
    pageSize: number = 50;

    footerData = {
        totalFees: 0,
        totalNet: 0,
        totalAmount: 0,
        categoryTotal: 0,
    };

    selectedPaymentIds: number[] = [];
    selectedPaymentGroups: PaymentGroup[] = [];

    private renderedData: PaymentGroup[];

    constructor(
            private _dialog: MatDialog,
            private _transactionsService: TransactionsService,
    ) {
    }

    get displayedColumns() {
        if (this.isOnMaster) {
            // for master, show id, hide fees, notes
            return [
                'select',
                'payorId',
                'payorName',
                'createdAt',
                'applied',
                'paymentMethodType',
                'actions',
            ];
        }

        if (this.isOnAdmin) {
            // for master, show id, hide fees, notes
            return [
                'select',
                'organizationId',
                'organization',
                'payorId',
                'payorName',
                'createdAt',
                'applied',
                'paymentMethodType',
                'actions',
            ];
        }

        // on dashboard, hide fees and notes
        if (this.isOnDashboard) {
            return [
                'payorName',
                'createdAt',
                'applied',
                'paymentMethodType',
                'actions',
            ];
        }

        // on unit dashboard, hide fees, notes, payor
        if (this.isOnUnitDashboard) {
            return [
                'createdAt',
                'applied',
                'paymentMethodType',
                'notes',
                'actions',
            ];
        }

        if (this.isOnMapModal) {
            return [
                'select',
                'payorName',
                'createdAt',
                'applied',
                'paymentMethodType',
                'notes',
            ];
        }

        return [
            'select',
            'payorName',
            'createdAt',
            'depositDate',
            'applied',
            'absorbedFees',
            'net',
            ...(this.tableDataSource.filteringByCategory ? ['categoryAmount'] : []),
            'paymentMethodType',
            'notes',
            'actions',
        ];
    }

    ngOnInit() {
        this.jwt = LegFiJwtService.read();
        this.isOnMaster = LegFiJwtService.isMasterOrg(this.jwt.orgId);

        this.tableDataSource.connect().pipe(untilDestroyed(this)).subscribe((data) => {
            this.renderedData = data;
        });
        this.tableDataSource.meta$.pipe(untilDestroyed(this)).subscribe((meta: PaymentsPaginationMeta) => {
            if (meta) {
                const {totalAmount, totalFees, totalNet, categoryTotal} = meta;
                this.footerData = {...this.footerData, totalAmount, totalFees, totalNet, categoryTotal};
            }
        });

        // on dashboard, hide fees and notes
        if (this.isOnDashboard) {
            this.pageSizeOptions = [50];
            this.pageSize = 50;
        }
        if (this.isOnMapModal) {
            this.pageSizeOptions = [10];
            this.pageSize = 10;
        }
    }

    ngAfterViewInit() {
        this.sort.sortChange.subscribe(() => {
            this.matPaginator.pageIndex = 0;
        });

        merge(this.sort.sortChange, this.matPaginator.page)
                .pipe(untilDestroyed(this))
                .pipe(
                        tap(() => {
                            this.loadPayments.emit();
                        }),
                ).subscribe();
    }

    /** Whether the number of selected elements matches the total number of rows. */
    isAllSelected() {
        const selectable = this.renderedData.filter((p) => p.deletable);
        const numSelected = this.selection.selected.length;
        return numSelected === selectable.length;
    }

    /** Selects all rows if they are not all selected; otherwise clear selection. */
    masterToggle() {
        if (this.isAllSelected()) {
            this.selection.clear();
        } else {
            this.renderedData.forEach(row => {
                if (row.deletable) {
                    this.selection.select(row);
                }
            });
        }
        this.selectedPaymentGroups = this.selection.selected;
        this.emitSelectedPayments();
    }

    rowSelected(row: PaymentGroup, checkbox) {
        this.selection.toggle(row);
        if (checkbox.checked) {
            this.selectedPaymentGroups.push(row);
        } else {
            const index = this.selectedPaymentGroups.findIndex(o => o.id === row.id);
            if (index > -1) {
                this.selectedPaymentGroups.splice(index, 1);
            }
        }
        this.emitSelectedPayments();
    }

    openDepositDetail(tx: Transaction) {
        const loading = this._dialog.open(LoadingDialogComponent, {
            data: <LoadingDialogInput>{
                title: 'Loading Deposit Details...',
            },
        });
        this._transactionsService.getTransactions(this.jwt.orgId, tx.id.toString())
                .pipe(untilDestroyed(this))
                .subscribe((response: TransactionsResponse) => {
                    loading.close();
                    for (const t of response.data) {
                        if (t.id === tx.id) {
                            const data: DepositDetailDialogInput = {
                                transaction: t,
                                bankAccountName: response.meta.included.owners[t.ownerId].friendlyName,
                            };
                            this._dialog.open(DepositDetailDialogComponent, {
                                width: MatDialogSizes.MD,
                                data: data,
                            });
                            break;
                        }
                    }
                });
    }

    private emitSelectedPayments() {
        this.selectedPaymentIds = this.selectedPaymentGroups.map(p => p.id);
        this.selectionMade.emit(this.selectedPaymentGroups);
    }

}
